import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signIn as amplifySignIn, signUp as amplifySignUp, signOut as amplifySignOut, confirmSignUp, getCurrentUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import useAuthStore from '../store/useAuthStore';
import useAppStore from '../store/useAppStore';
import { syncService } from '../data/syncService';
import db from '../data/db';

// Wipe all per-user local state on sign-out / account-deletion. Theme
// preference (Zustand `nightMode`) is intentionally preserved. Best-effort
// final sync first so dirty work isn't silently dropped if the network is up.
async function clearLocalUserData() {
  try { await syncService.syncAll(); } catch { /* offline — accept the loss */ }
  try {
    await db.decks.clear();
    await db.cards.clear();
    await db.reviews.clear();
    await db.stats.clear();
    await db.settings.clear();
    if (db.table('syncLog')) await db.syncLog.clear();
  } catch { /* db may already be closed */ }
  try { useAppStore.getState().resetUserState(); } catch { /* ignore */ }
}

const API_ENDPOINT = 'https://8vuu2yzow2.execute-api.eu-west-2.amazonaws.com/flashcards';

async function isNativeIOS() {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  // Keep auth state in sync with OAuth sign-in / sign-out events
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        checkUser();
      }
      if (payload.event === 'signedOut') {
        setUser(null);
        setIsAuthenticated(false);
        useAuthStore.getState().clearAuth();
      }
    });
    return unsubscribe;
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);
      useAuthStore.getState().setAuth(currentUser);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      useAuthStore.getState().clearAuth();
    }
    setLoading(false);
  }

  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await amplifySignIn({ username: email, password });
      if (result.isSignedIn) {
        await checkUser();
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    setError(null);
    try {
      const result = await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: { email, name },
        },
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const confirmAccount = useCallback(async (email, code) => {
    setError(null);
    try {
      const result = await confirmSignUp({ username: email, confirmationCode: code });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    setError(null);
    try {
      await clearLocalUserData();
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      useAuthStore.getState().clearAuth();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Apple Sign In — native on iOS (no browser), OAuth redirect on web
  const signInWithApple = useCallback(async () => {
    setError(null);
    let step = 'init';
    try {
      const native = await isNativeIOS();
      step = `platform=${native ? 'native' : 'web'}`;

      if (native) {
        step = 'importing capacitor';
        const { registerPlugin } = await import('@capacitor/core');
        step = 'registering plugin';
        const AppleSignIn = registerPlugin('AppleSignIn');
        step = 'calling signIn';
        const result = await AppleSignIn.signIn();
        step = 'got Apple result';
        const { identityToken, email, fullName } = result;
        if (!identityToken) throw new Error('No identityToken from Apple');
        const givenName = fullName?.split(' ')[0] || '';
        const familyName = fullName?.split(' ').slice(1).join(' ') || '';

        step = 'calling backend';
        const res = await fetch(`${API_ENDPOINT}/auth/apple`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identityToken, email, givenName, familyName }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Backend ${res.status}`);
        }
        step = 'got backend credentials';
        const { username, password } = await res.json();

        step = 'clearing stale session';
        try { await amplifySignOut(); } catch { /* ignore */ }

        step = 'amplify signIn';
        const signInResult = await amplifySignIn({ username, password });
        if (signInResult.isSignedIn) {
          await checkUser();
        }
      } else {
        await signInWithRedirect({ provider: { custom: 'SignInWithApple' } });
      }
    } catch (err) {
      if (err?.message?.includes('cancel')) return;
      setError(`[${step}] ${err.message}`);
      throw err;
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setError(null);
    try {
      const { api } = await import('../data/apiClient');
      await api.deleteAccount();
      // Clear local IndexedDB tables + Zustand user state (same path as
      // sign-out — but we skip the pre-emptive sync since the account is
      // about to be wiped server-side anyway).
      try {
        await db.decks.clear();
        await db.cards.clear();
        await db.reviews.clear();
        await db.stats.clear();
        await db.settings.clear();
        if (db.table('syncLog')) await db.syncLog.clear();
      } catch { /* db may already be closed */ }
      try { useAppStore.getState().resetUserState(); } catch { /* ignore */ }
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      useAuthStore.getState().clearAuth();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    signIn,
    signUp,
    confirmAccount,
    signOut: signOutUser,
    signInWithApple,
    deleteAccount,
    getToken,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
